/*
 * MOTION DESIGN LTD CONFIDENTIAL
 *
 * [2020] Motion Design Ltd All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Motion Design Ltd. The intellectual and technical concepts contained
 * herein are proprietary to Motion Design Ltd. and may be covered by N.Z.
 * and Foreign Patents, patents in process, and are protected by trade secret
 * or copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Motion Design Ltd.
 */

import {ROLE} from '@/constants/role'
import {fetchClientFactory} from '@motiondesign/npm-common-utils/dataclient/fetch'
import {setLoginSuccessRedirect, setUserAuthenticated} from '@/auth/authContext'
import {differenceInDays, differenceInHours, differenceInMinutes} from 'date-fns'
import {SortingState} from '@tanstack/react-table'
import {sortingStateToObject} from '@/components/DataTable'

/**
 * Recursively prefixes nested API-descriptor paths with their parent paths.
 *
 * Mutates the descriptor tree during API_SCHEMA.init so every fetch helper can use a complete relative path.
 */
export function prependPath(object: any, path?: string) {
    Object.keys(object).forEach((key) => {
        if (typeof object[key] === 'object' && 'path' in object[key]) {
            let prependedPath = (path ?? '') + '/' + object[key].path
            object[key].path = prependedPath

            prependPath(object[key], prependedPath)
        }
    })
}

/** Generic paginated backend response returned by list endpoints. */
export interface Paginated<T> {
    page: {
        pageNumber: number
        elementsOnPage: number
        totalElements: number
        totalPages: number
    }
    data: T[]
}

export const NEW_CLIENT_URL_PARAM = 'new'

/**
 * Returns the number of days since the given date.
 * Rounds up.
 */
export function getDaysSince(someDate: Date): number {
    const currentDate = new Date()
    const differenceInTime = currentDate.getTime() - someDate.getTime()
    return Math.ceil(differenceInTime / (1000 * 3600 * 24))
}

/**
 * Returns the number of days or hours or mins since the given date in text form.
 * Example: 3 day(s) ago, 5 hr(s) ago, 45 min(s) ago
 */
export function getDaysOrTimeSinceMsg(someDate: Date): string {
    // Current date and time
    const now = new Date()

    // Calculate the total difference in days, hours, and minutes
    const diffDays = differenceInDays(now, someDate)
    const diffHours = differenceInHours(now, someDate)
    const diffMinutes = differenceInMinutes(now, someDate)

    // Determine the largest unit of time to display
    if (diffDays > 0) {
        return `${diffDays} day(s) ago`
    } else if (diffHours > 0) {
        return `${diffHours} hour(s) ago`
    } else {
        return `${diffMinutes} minute(s) ago`
    }
}

/**
 * Shared authenticated fetch helpers. They parse ISO dates and redirect to login after an expired session so
 * individual API operations do not need to repeat that behaviour.
 */
const {getOrGoLogin, postOrGoLogin, putOrGoLogin, deleteOrGoLogin} = fetchClientFactory(
    () => {
        setUserAuthenticated(false)
        if (!window.location.pathname.includes('login')) {
            setLoginSuccessRedirect(window.location.pathname)
            window.location.reload()
        }
    },
    {autoParseDates: true},
)

/** Number of days without a machine check-in after which the UI flags it as stale. */
export const LAST_SEEN_PROBLEM_DAYS = 5

/**
 * Builds Squid's nested API client. Each leaf combines a backend path with a typed helper that must use the
 * shared authenticated fetch client above.
 */
const API_SCHEMA = (version: string) => ({
    path: '',
    API: {
        path: 'api',
        APPLICATIONS: {
            path: 'applications',
            APPLICATION: {
                path: ':applicationId',
                UPLOAD_VERSION: {
                    path: 'upload-version',
                    /** Returns the legacy upload-and-release path for an application. */
                    getPath(applicationId: number) {
                        return this.path.replace(':applicationId', applicationId.toString())
                    },
                },
            },
        },
    },
    APP: {
        path: 'app',
        LOGIN: {
            path: 'login',
        },
        LOGOUT: {
            path: 'logout',
            /** Ends the active browser session through the authenticated API client. */
            goLogout: function () {
                return postOrGoLogin(this, {})
            },
        },
        USERS: {
            path: 'users',
            /** Lists users and their application permissions for administrator management. */
            list: function (): Promise<UserInfo[]> {
                return getOrGoLogin(this)
            },
            /** Replaces administrator-managed user permissions and active/admin flags. */
            save: function (users: UserInfo[]): Promise<UserInfo[]> {
                return postOrGoLogin(this, users)
            },
        },
        USER: {
            path: 'user',
            PROFILE: {
                path: 'profile',
                /** Returns the signed-in user's profile and permitted applications. */
                get: function (): Promise<UserInfo> {
                    return getOrGoLogin(this)
                },
                AVATAR: {
                    path: 'avatar',
                    /** Builds the avatar URL for a user's stored profile image. */
                    get(email: string) {
                        return `${this.path}?email=${email}`
                    },
                },
            },
        },
        CUSTOMERS: {
            path: 'customers',
            /** Lists customers available for configuration and release targeting. */
            list: function (): Promise<NamedModel[]> {
                return getOrGoLogin(this)
            },
            /** Replaces the administrator-managed customer list. */
            save: function (customer: NamedModel[]): Promise<NamedModel[]> {
                return postOrGoLogin(this, customer)
            },
            RELEASE_SETTINGS: {
                path: ':customerId/release-settings',
                /** Returns the release display time zone and notification recipients for one customer. */
                get: function (customerId: number): Promise<CustomerReleaseSettingsDto> {
                    return getOrGoLogin({...this, path: this.path.replace(':customerId', customerId.toString())})
                },
                /** Replaces a customer's release and automatic-update failure notification settings. */
                save: function (settings: CustomerReleaseSettingsDto): Promise<CustomerReleaseSettingsDto> {
                    return putOrGoLogin(
                        {...this, path: this.path.replace(':customerId', settings.customerId.toString())},
                        settings,
                    )
                },
            },
        },
        TYPES: {
            path: 'types',
            CLIENTS: {
                path: 'clients',
                /** Lists client-related reference types used by configuration controls. */
                list: function (): Promise<NamedModel[]> {
                    return getOrGoLogin(this)
                },
            },
            APPLICATIONS: {
                path: 'applications',
                /** Lists editable application definitions. */
                list: function (): Promise<ApplicationDto[]> {
                    return getOrGoLogin(this)
                },
                /** Replaces application definitions after server-side validation. */
                save: function (applications: ApplicationDto[]): Promise<ApplicationDto[]> {
                    return postOrGoLogin(this, applications)
                },
            },
            RELEASE_TYPES: {
                path: 'release-types',
                /** Lists Release Types globally or for one application. */
                list: function (applicationId: number | null = null): Promise<ReleaseTypeWithVersion[]> {
                    const options = applicationId ? {applicationId} : {}
                    return getOrGoLogin(this, options)
                },
                /** Replaces the available Release Types. */
                save: function (applications: NamedModel[]): Promise<NamedModel[]> {
                    return postOrGoLogin(this, applications)
                },
            },
            APP_RELEASES: {
                path: 'application-release-types',
                /** Lists Application-to-Release-Type configuration associations. */
                list: function (): Promise<AppReleasesDto[]> {
                    return getOrGoLogin(this)
                },
                /** Replaces Application-to-Release-Type configuration associations. */
                save: function (applications: AppReleasesDto[]): Promise<AppReleasesDto[]> {
                    return postOrGoLogin(this, applications)
                },
            },
        },
        CLIENTS: {
            path: 'clients',
            /** Lists machines with optional filtering, search, pagination, and server-side sorting. */
            list: function (
                page?: number,
                size?: number,
                searchQuery?: string,
                filterByApplication?: string,
                filterByReleaseType?: string,
                filterByCustomer?: string,
                sorting?: SortingState,
            ): Promise<Paginated<ClientDto>> {
                return getOrGoLogin(this, {
                    page,
                    size,
                    searchQuery,
                    filterByApplication,
                    filterByReleaseType,
                    filterByCustomer,
                    ...sortingStateToObject(sorting),
                })
            },

            DETAILS: {
                path: 'details',
                /** Loads one machine's editable configuration. */
                get: function (id: number): Promise<ClientDto> {
                    return getOrGoLogin(this, {id})
                },
                /** Creates or updates a machine configuration. */
                save: function (formData: any): Promise<ClientDto> {
                    return postOrGoLogin(this, formData)
                },
                /** Deletes a machine and its associated token/support records. */
                delete: function (id: number): Promise<ClientDto> {
                    return deleteOrGoLogin(this, {id})
                },
            },

            TOKENS: {
                path: 'tokens',
                /** Rotates a machine token and returns its one-time plaintext value. */
                reset: function (id: number | string): Promise<string> {
                    return getOrGoLogin(this, {id})
                },
            },

            AVAILABLE_VERSION: {
                path: 'available-version',
                /** Returns the legacy global version for an Application/Release Type pair. */
                get: function (applicationId: number, releaseTypeId: number): Promise<VersionDto> {
                    return getOrGoLogin(this, {applicationId, releaseTypeId})
                },
            },
            RELEASE_TARGETING: {
                path: ':clientId/release-targeting',
                /** Loads a machine's resolved version, group membership, schedules, and recipient scopes. */
                get: function (clientId: number, signal?: AbortSignal): Promise<ClientReleaseTargetingDto> {
                    return getOrGoLogin(
                        {...this, path: this.path.replace(':clientId', clientId.toString())},
                        undefined,
                        {signal},
                    )
                },
            },
            UPDATE_SCHEDULE: {
                path: ':clientId/update-schedule',
                /** Sets or clears a machine-specific not-before time and returns refreshed targeting. */
                save: function (clientId: number, updateAfter: Date | null): Promise<ClientReleaseTargetingDto> {
                    return putOrGoLogin(
                        {...this, path: this.path.replace(':clientId', clientId.toString())},
                        {updateAfter},
                    )
                },
            },
            CUSTOMER_GROUP_MEMBERSHIPS: {
                path: ':clientId/customer-group-memberships',
                /** Calculates the effective version and schedule impact before changing group memberships. */
                preview: function (
                    clientId: number,
                    request: CustomerGroupMembershipMutationDto,
                ): Promise<CustomerGroupMembershipImpactDto> {
                    return postOrGoLogin(
                        {...this, path: `${this.path.replace(':clientId', clientId.toString())}/impact-preview`},
                        request,
                    )
                },
                /** Applies a membership change only when its preview hash is still current. */
                save: function (
                    clientId: number,
                    request: CustomerGroupMembershipMutationDto,
                ): Promise<ClientReleaseTargetingDto> {
                    return putOrGoLogin({...this, path: this.path.replace(':clientId', clientId.toString())}, request)
                },
            },
            FAILURE_RECIPIENTS: {
                path: ':clientId/failure-recipients',
                /** Replaces the administrator-managed automatic-update failure recipients for a machine. */
                save: function (
                    clientId: number,
                    settings: FailureRecipientSettingsDto,
                ): Promise<FailureRecipientSettingsDto> {
                    return putOrGoLogin({...this, path: this.path.replace(':clientId', clientId.toString())}, settings)
                },
            },
        },
        APPLICATIONS: {
            path: 'applications',
            /** Lists application configuration, Release Types, and global display versions. */
            list: function (
                page?: number,
                size?: number,
                searchQuery?: string,
                sorting?: SortingState,
            ): Promise<Paginated<AppReleaseVersionsDto>> {
                return getOrGoLogin(this, {
                    page,
                    size,
                    searchQuery,
                    ...sortingStateToObject(sorting),
                })
            },
            /** Applies Applications-page global release-pointer selections. */
            save: function (applications: AppReleaseVersionsDto[]): Promise<void> {
                return postOrGoLogin(this, applications)
            },
            VERSIONS: {
                path: ':applicationId/versions',
                /** Lists reusable uploaded artifacts; uploading and releasing are separate operations. */
                list: function (applicationId: number, signal?: AbortSignal): Promise<UploadedVersionDto[]> {
                    return getOrGoLogin(
                        {...this, path: this.path.replace(':applicationId', applicationId.toString())},
                        undefined,
                        {signal},
                    )
                },
            },
            FAILURE_RECIPIENTS: {
                path: ':applicationId/failure-recipients',
                /** Returns automatic-update failure recipients configured directly on an application. */
                get: function (applicationId: number): Promise<FailureRecipientSettingsDto> {
                    return getOrGoLogin({...this, path: this.path.replace(':applicationId', applicationId.toString())})
                },
                /** Replaces automatic-update failure recipients configured directly on an application. */
                save: function (
                    applicationId: number,
                    settings: FailureRecipientSettingsDto,
                ): Promise<FailureRecipientSettingsDto> {
                    return putOrGoLogin(
                        {...this, path: this.path.replace(':applicationId', applicationId.toString())},
                        settings,
                    )
                },
            },
        },
        DEPLOYMENT_GROUPS: {
            path: 'deployment-groups',
            /** Lists active Deployment Groups, optionally for one customer and Release Type. */
            list: function (customerId?: number, releaseTypeId?: number): Promise<DeploymentGroupDto[]> {
                return getOrGoLogin(this, {customerId, releaseTypeId})
            },
            /** Creates a Deployment Group after its impact has been previewed and confirmed. */
            create: function (request: DeploymentGroupMutationDto): Promise<DeploymentGroupDto> {
                return postOrGoLogin(this, request)
            },
            /** Calculates the effect of creating a Deployment Group without persisting it. */
            previewCreate: function (request: DeploymentGroupMutationDto): Promise<DeploymentGroupImpactDto> {
                return postOrGoLogin({...this, path: `${this.path}/impact-preview`}, request)
            },
            DETAILS: {
                path: ':groupId',
                /** Loads one Deployment Group's members, recipients, schedules, and released versions. */
                get: function (groupId: number): Promise<DeploymentGroupDto> {
                    return getOrGoLogin({...this, path: this.path.replace(':groupId', groupId.toString())})
                },
                /** Updates a Deployment Group when the supplied preview hash remains current. */
                save: function (groupId: number, request: DeploymentGroupMutationDto): Promise<DeploymentGroupDto> {
                    return putOrGoLogin({...this, path: this.path.replace(':groupId', groupId.toString())}, request)
                },
                /** Archives a non-default Deployment Group when the caller confirms its impact hash. */
                delete: function (groupId: number, impactHash?: string): Promise<void> {
                    return deleteOrGoLogin(
                        {...this, path: this.path.replace(':groupId', groupId.toString())},
                        {impactHash},
                    )
                },
                /** Calculates the effect of changing a Deployment Group without persisting it. */
                preview: function (
                    groupId: number,
                    request: DeploymentGroupMutationDto,
                ): Promise<DeploymentGroupImpactDto> {
                    return postOrGoLogin(
                        {...this, path: `${this.path.replace(':groupId', groupId.toString())}/impact-preview`},
                        request,
                    )
                },
                /** Calculates the effect of archiving a Deployment Group without persisting it. */
                previewArchive: function (groupId: number): Promise<DeploymentGroupImpactDto> {
                    return getOrGoLogin({
                        ...this,
                        path: `${this.path.replace(':groupId', groupId.toString())}/archive-impact-preview`,
                    })
                },
            },
        },
        RELEASES: {
            path: 'releases',
            /** Loads the effective-version coverage matrix for applications visible to the current user. */
            coverage: function (): Promise<ReleaseCoverageDto> {
                return getOrGoLogin({...this, path: `${this.path}/coverage`})
            },
            /** Lists permission-filtered release audit history. */
            history: function (): Promise<ReleaseHistoryDto[]> {
                return getOrGoLogin({...this, path: `${this.path}/history`})
            },
            /** Fetches changelog and Jira metadata for a release-history event. */
            eventMetadata: function (releaseEventId: number, signal?: AbortSignal): Promise<ReleaseEventMetadataDto> {
                return getOrGoLogin({...this, path: `${this.path}/events/${releaseEventId}/metadata`}, undefined, {
                    signal,
                })
            },
            /** Calculates affected machines, notifications, and a confirmation hash for a proposed release. */
            preview: function (request: ReleaseMutationDto): Promise<ReleasePreviewDto> {
                return postOrGoLogin({...this, path: `${this.path}/preview`}, request)
            },
            /** Applies a release only when the request contains the current preview hash. */
            create: function (request: ReleaseMutationDto): Promise<ReleaseResultDto> {
                return postOrGoLogin(this, request)
            },
            /** Calculates the version effect of clearing a machine override without changing it. */
            previewClearMachineOverride: function (clientId: number): Promise<ClearMachineOverridePreviewDto> {
                return getOrGoLogin({
                    ...this,
                    path: `${this.path}/machine-overrides/${clientId}/clear-impact-preview`,
                })
            },
            /** Clears a direct machine override when the caller confirms the preview hash. */
            clearMachineOverride: function (clientId: number, impactHash: string): Promise<void> {
                return deleteOrGoLogin({...this, path: `${this.path}/machine-overrides/${clientId}`}, {impactHash})
            },
            /** Fetches a rollout's machine snapshots and notification delivery state. */
            rollout: function (rolloutId: number): Promise<ReleaseRolloutDto> {
                return getOrGoLogin({...this, path: `${this.path}/rollouts/${rolloutId}`})
            },
            /** Requeues a failed notification delivery for an administrator-requested retry. */
            retryNotification: function (deliveryId: number): Promise<void> {
                return postOrGoLogin({...this, path: `${this.path}/notifications/${deliveryId}/retry`}, {})
            },
            /** Fetches the receipt, release, and installation timeline for an uploaded version. */
            versionTimeline: function (versionId: number): Promise<VersionReleaseTimelineDto> {
                return getOrGoLogin({...this, path: `${this.path}/timeline/versions/${versionId}`})
            },
            /** Fetches administrator-managed release settings and selectable IANA time zones. */
            settings: function (): Promise<ReleaseSettingsDto> {
                return getOrGoLogin({...this, path: `${this.path}/settings`})
            },
            /** Fetches the system time zone used to render release timestamps for every user. */
            displaySettings: function (): Promise<ReleaseDisplaySettingsDto> {
                return getOrGoLogin({...this, path: `${this.path}/display-settings`})
            },
            /** Replaces administrator-managed release display and global failure-recipient settings. */
            saveSettings: function (settings: ReleaseSettingsMutationDto): Promise<ReleaseSettingsDto> {
                return putOrGoLogin({...this, path: `${this.path}/settings`}, settings)
            },
        },
        SUPPORT: {
            path: 'support',
        },
        CONFIG: {
            path: 'config',

            API_TOKEN: {
                path: 'api-keys',
                /** Lists API-token metadata without exposing token secrets. */
                list: function (): Promise<ApiKeyDto[]> {
                    return getOrGoLogin(this)
                },
                /** Creates an API token and returns its one-time plaintext value. */
                create: function (name: string): Promise<string> {
                    return putOrGoLogin(this, {name})
                },
                /** Revokes an API token by identifier. */
                delete: function (id: number) {
                    return deleteOrGoLogin(this, {id})
                },
            },
        },
        NOTIFICATIONS: {
            path: 'notifications',
        },
    },
    /** Resolves every nested relative path before exposing this client to the application. */
    init: function () {
        prependPath(this, this.path)
        return this
    },
})

/** Backend route-version prefix used when building the shared API client. */
export const DEFAULT_API_VERSION = 'v1'
/** Shared, fully initialised API client used by all UI data loading and mutations. */
export const API = API_SCHEMA(DEFAULT_API_VERSION).init()

/** Default date-time format for legacy UI displays that do not receive an explicit display zone. */
export const DEFAULT_DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm:ss'

/** Authenticated user's profile and application-level access assignments. */
export interface UserInfo extends NamedModel {
    email: string
    fullName: string
    active: boolean
    admin: boolean
    roles: ROLE[]
    applicationAccess: ApplicationDto[]
}

/** Browser configuration provided by the backend at application startup. */
export interface Config {
    gRecaptcha: {
        siteKey: string
    }
    sentry: {
        environment: string
        enabled: boolean
    }
}

/** Makes selected properties nullable while retaining all other properties from a model. */
export type WithNullable<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

/** Lightweight identifier-and-display-name reference embedded in API responses. */
export interface NamedModel {
    id: number | null
    name: string
}

/** Metadata for an API token; its secret is intentionally never returned after creation. */
export interface ApiKeyDto {
    id: number
    name: string
    createdBy: string
    createdDate: Date
    lastUsedTimeStamp: Date | null
}

/** A configured Release Type and the globally selected display version for one application. */
export interface ReleaseTypeWithVersion {
    id: number
    name: string
    displayVersion: NamedModel | null
}

/** Editable application definition, including its stable artifact filename and storage directory. */
export interface ApplicationDto {
    id: number | null
    name: string
    fileName: string
    directory: string
}

/** Application-to-Release-Type associations used by the configuration editor. */
export interface AppReleasesDto {
    appName: string
    appId: number
    releases: NamedModel[]
}

/** Applications-page row containing upload choices and global Release Type selections. */
export interface AppReleaseVersionsDto {
    appName: string
    appId: number
    versions: NamedModel[]
    releaseTypes: ReleaseTypeWithVersion[]
}

/** Machine configuration and live status used by the client-management views. */
export interface ClientDto {
    id: number | null
    name: string
    customer: NamedModel
    releaseType: NamedModel | null
    application: NamedModel
    currentVersion: string | null
    availableVersion: string | null
    enabled: boolean
    autoUpdate: boolean
    lastSeen: Date
    internalIpAddress: string | null
    externalIpAddress: string | null
    updateAfter: Date | null
    remoteSupportEnabled: boolean
}

/** Current global version information for an Application/Release Type pair. */
export interface VersionDto {
    version: string
    releaseDate: Date | null
}

/**
 * Targeting layers supported by the release API. These values must remain aligned with the backend release target type.
 */
export const RELEASE_TARGET_TYPE = {
    GLOBAL: 'GLOBAL',
    GROUP: 'GROUP',
    MACHINE: 'MACHINE',
} as const

/** Targeting layer changed by a release mutation. */
export type ReleaseTargetType = (typeof RELEASE_TARGET_TYPE)[keyof typeof RELEASE_TARGET_TYPE]
/** Effective machine state shown in the release-coverage view. */
export type ReleaseCoverageStatus = 'LIVE' | 'WAITING' | 'AVAILABLE' | 'DISABLED' | 'UNRELEASED'

/** Reusable uploaded artifact version and its extracted release metadata. */
export interface UploadedVersionDto {
    id: number
    applicationId: number
    version: string
    fileName: string
    uploadedAt: Date
    changelogMarkdown: string | null
    jiraTicketDefaults: string[]
    created: boolean
}

/** One application version released through a Deployment Group. */
export interface DeploymentGroupVersionDto {
    application: NamedModel
    applicationReleaseTypeId: number
    version: NamedModel
    releasedAt: Date
}

/**
 * Full persisted Deployment Group configuration, including its scope, members, recipients, and released versions.
 *
 * `updateAfter` is an optional group-level not-before instant inherited by compatible members without a
 * machine-specific schedule.
 */
export interface DeploymentGroupDto {
    id: number
    customer: NamedModel | null
    scope: 'CUSTOMER' | 'CROSS_CUSTOMER' | 'DEFAULT'
    releaseType: NamedModel
    name: string
    isDefault: boolean
    timeZoneOverride: string | null
    displayTimeZone: string
    updateAfter: Date | null
    memberClientIds: number[]
    recipients: string[]
    releasedVersions: DeploymentGroupVersionDto[]
    createdAt: Date
}

/** Create or update request for a Deployment Group; impactHash confirms a reviewed preview. */
export interface DeploymentGroupMutationDto {
    customerId: number | null
    releaseTypeId: number
    name: string
    timeZoneOverride: string | null
    updateAfter: Date | null
    memberClientIds: number[]
    recipients: string[]
    impactHash?: string
}

/** Per-machine version and schedule difference calculated by a Deployment Group impact preview. */
export interface DeploymentGroupClientImpactDto {
    clientId: number
    clientName: string
    currentAvailableVersion: string | null
    resultingAvailableVersion: string | null
    previousEffectiveUpdateAfter: Date | null
    resultingEffectiveUpdateAfter: Date | null
}

/** Non-persistent impact preview for creating, changing, or archiving a Deployment Group. */
export interface DeploymentGroupImpactDto {
    impactHash: string
    firstActiveGroup: boolean
    lastActiveGroup: boolean
    assignedClientIds: number[]
    affectedClients: DeploymentGroupClientImpactDto[]
    sentNotificationsRemain: boolean
}

/** Customer-specific release time zone and normal/failure notification recipients. */
export interface CustomerReleaseSettingsDto {
    customerId: number
    timeZone: string
    recipients: string[]
    failureRecipients: string[]
}

/** Notification recipient list at a machine, application, customer, or global failure scope. */
export interface FailureRecipientSettingsDto {
    recipients: string[]
}

/**
 * Full effective release-targeting state for one machine.
 *
 * effectiveSource identifies whether the available version is global, group-derived, or a direct override.
 */
export interface ClientReleaseTargetingDto {
    installedVersionId: number | null
    installedVersion: string | null
    installedAt: Date | null
    availableVersionId: number | null
    availableVersion: string | null
    availableAt: Date | null
    effectiveVersion: string | null
    effectiveSource: string
    groups: {
        id: number
        name: string
        isDefault: boolean
        scope: 'CUSTOMER' | 'CROSS_CUSTOMER' | 'DEFAULT'
        releasedVersion: string | null
        updateAfter: Date | null
        displayTimeZone: string
        providesAvailableVersion: boolean
    }[]
    directOverride: {
        versionId: number
        version: string
        contextGroup: NamedModel | null
        createdAt: Date
        providesAvailableVersion: boolean
    } | null
    effectiveUpdateAfter: Date | null
    effectiveScheduleSource: 'MACHINE_OVERRIDE' | 'DEPLOYMENT_GROUP' | 'IMMEDIATE'
    effectiveScheduleGroupIds: number[]
    displayTimeZone: string
    systemTimeZone: string
    targetingIdentityLocked: boolean
    failureRecipients: string[]
    applicationFailureRecipients: string[]
    customerFailureRecipients: string[]
    supportFailureRecipients: string[]
}

/** Request to replace one machine's named customer Deployment Group memberships. */
export interface CustomerGroupMembershipMutationDto {
    groupIds: number[]
    impactHash?: string
}

/** Preview of the version and schedule changes caused by a membership mutation. */
export interface CustomerGroupMembershipImpactDto {
    impactHash: string
    previousGroupIds: number[]
    resultingGroupIds: number[]
    addedGroups: NamedModel[]
    removedGroups: NamedModel[]
    currentAvailableVersion: string | null
    resultingAvailableVersion: string | null
    previousEffectiveUpdateAfter: Date | null
    resultingEffectiveUpdateAfter: Date | null
    sentNotificationsRemain: boolean
}

/**
 * Client request to make a version available globally, to a Deployment Group, or directly to a machine.
 *
 * The optional impactHash must be supplied from a release preview when applying the mutation.
 */
export interface ReleaseMutationDto {
    applicationId: number
    releaseTypeId: number
    versionId: number
    targetType: ReleaseTargetType
    targetId?: number
    contextGroupId?: number
    changelogMarkdown: string
    jiraTickets: string[]
    impactHash?: string
}

/** The expected availability and installation result for one machine in a proposed release. */
export interface ReleaseClientImpactDto {
    clientId: number
    clientName: string
    enabled: boolean
    autoUpdate: boolean
    currentVersion: string | null
    previousAvailableVersion: string | null
    resultingAvailableVersion: string | null
    outcome: string
}

/** Read-only release preview whose impactHash guards the subsequent create operation against stale targeting. */
export interface ReleasePreviewDto {
    impactHash: string
    application: NamedModel
    releaseType: NamedModel
    version: NamedModel
    targetType: ReleaseTargetType
    targetLabel: string
    recipients: string[]
    notificationTypes: string[]
    clients: ReleaseClientImpactDto[]
}

/** Audit event and rollout records created after a release is successfully applied. */
export interface ReleaseResultDto {
    releaseEventId: number
    rolloutIds: number[]
    releasedAt: Date
}

/** Deployment Group state embedded in one row of the effective-version coverage matrix. */
export interface ReleaseCoverageGroupDto {
    id: number
    name: string
    isDefault: boolean
    scope: 'CUSTOMER' | 'CROSS_CUSTOMER' | 'DEFAULT'
    releasedVersionId: number | null
    releasedVersion: string | null
    memberClientIds: number[]
}

/** Machine state embedded in one row of the effective-version coverage matrix. */
export interface ReleaseCoverageMachineDto {
    id: number
    name: string
    customer: NamedModel
    enabled: boolean
    autoUpdate: boolean
    currentVersion: string | null
    availableVersion: string | null
    effectiveSource: string
    groupIds: number[]
    status: ReleaseCoverageStatus
}

/** Coverage for one Application/Release Type and optional customer scope. */
export interface ReleaseCoverageRowDto {
    application: NamedModel
    releaseType: NamedModel
    customer: NamedModel | null
    applicationReleaseTypeId: number
    globalVersion: string | null
    groups: ReleaseCoverageGroupDto[]
    machines: ReleaseCoverageMachineDto[]
}

/** Effective-version coverage matrix visible to the authenticated user. */
export interface ReleaseCoverageDto {
    rows: ReleaseCoverageRowDto[]
}

/** Permission-filtered audit-history entry for a release or release-clearing event. */
export interface ReleaseHistoryDto {
    id: number
    versionId: number | null
    applicationId: number
    applicationName: string
    releaseTypeId: number
    releaseTypeName: string
    customerIds: number[]
    version: string
    targetType: ReleaseTargetType
    deploymentGroupId: number | null
    targetLabel: string
    source: string
    releasedBy: string | null
    releasedAt: Date
    rolloutStatus: string | null
    rolloutIds: number[]
    rollouts: ReleaseRolloutSummaryDto[]
}

/** Changelog and Jira-ticket metadata associated with a release-history event. */
export interface ReleaseEventMetadataDto {
    id: number
    applicationId: number
    versionId: number | null
    version: string
    changelogMarkdown: string
    jiraTickets: string[]
}

/** Concise rollout state embedded in a release-history entry. */
export interface ReleaseRolloutSummaryDto {
    id: number
    customerName: string | null
    targetLabel: string
    status: string
}

/**
 * A machine snapshot inside a rollout, retained to explain installation and timeout outcomes after later
 * configuration changes.
 */
export interface ReleaseRolloutClientDto {
    clientId: number | null
    clientName: string
    customerName: string
    applicationName: string
    releaseTypeName: string
    expectedVersion: string
    autoUpdate: boolean
    status: string
    eligibleAt: Date | null
    deadline: Date | null
    lastCheckedAt: Date | null
    lastReportedVersion: string | null
    timedOutAt: Date | null
    timeoutLastCheckedAt: Date | null
    timeoutLastReportedVersion: string | null
    installedAt: Date | null
    endedAt: Date | null
    endReason: string | null
}

/** Persisted email delivery state, including retry scheduling and the last send error. */
export interface ReleaseNotificationDeliveryDto {
    id: number
    notificationType: string
    notificationReason: string
    addressingMode: 'TO' | 'BCC'
    recipientEmails: string[]
    subject: string | null
    status: string
    attemptCount: number
    nextAttemptAt: Date
    sentAt: Date | null
    lastError: string | null
}

/** Detailed rollout view with machine snapshots and notification deliveries. */
export interface ReleaseRolloutDto {
    id: number
    releaseEventId: number
    versionId: number | null
    application: NamedModel
    releaseType: NamedModel
    version: string
    customer: NamedModel | null
    targetType: ReleaseTargetType
    targetLabel: string
    changelogMarkdown: string
    jiraTickets: string[]
    jiraBaseUrl: string
    status: string
    systemTimeZone: string
    emailTimeZone: string | null
    releasedAt: Date
    deadline: Date | null
    completedAt: Date | null
    clients: ReleaseRolloutClientDto[]
    notifications: ReleaseNotificationDeliveryDto[]
}

/** Public system time-zone setting used to render release timestamps. */
export interface ReleaseDisplaySettingsDto {
    systemTimeZone: string
}

/** Preview of the effective version that would result from clearing a direct machine override. */
export interface ClearMachineOverridePreviewDto {
    impactHash: string
    clientId: number
    clientName: string
    currentOverrideVersion: string
    resultingAvailableVersion: string | null
    effectiveVersionChanges: boolean
}

/** Administrator-managed release display and global failure-recipient settings. */
export interface ReleaseSettingsDto {
    systemTimeZone: string
    availableTimeZones: string[]
    globalFailureRecipients: string[]
}

/** Administrator request to update the release display zone and global failure recipients. */
export interface ReleaseSettingsMutationDto {
    systemTimeZone: string
    globalFailureRecipients: string[]
}

/** Authorised version history from artifact receipt through releases and installations. */
export interface VersionReleaseTimelineDto {
    versionId: number
    version: string
    application: NamedModel
    receivedAt: Date
    receivedSource: string
    receivedBy: string | null
    releases: ReleaseHistoryDto[]
    installations: VersionInstallationTimelineDto[]
}

/** One machine installation of a version and the releases that made it relevant. */
export interface VersionInstallationTimelineDto {
    clientId: number | null
    clientName: string
    customerName: string
    installedAt: Date
    installedLate: boolean
    rollouts: {
        id: number
        targetLabel: string
    }[]
}

/** WebSocket endpoint templates used for remote-support terminal connections. */
export const WEBSOCKETS = {
    TERMINAL_WS_ENDPOINT: '/ws/terminal/{clientId}',
}

/** Current remote-support WebSocket connection state for a machine. */
export type MachineClientStatus = 'CONNECTED' | 'DISCONNECTED'

/** Union of messages the remote-support terminal may receive from the backend. */
export type WsMessageTypes = UpdateMachineClientStatusType | UpdateCommandResultType | SessionForceClosedType

/** Remote-support message that updates a terminal with machine connection state. */
export interface UpdateMachineClientStatusType {
    messageType: 'UPDATE_MACHINE_CLIENT_STATUS'
    payload: MachineClientStatus
}

/** Remote-support message carrying a command result returned by a machine. */
export interface UpdateCommandResultType {
    messageType: 'UPDATE_COMMAND_RESULT'
    payload: string
}

/** Remote-support message instructing a terminal to close its current support session. */
export interface SessionForceClosedType {
    messageType: 'SESSION_FORCE_CLOSED'
    payload: string
}
